/**
 * Camada 1 · VisualizadorPdf: o PDF desenhado PELA PLATAFORMA, e nao pelo navegador.
 *
 * O PEDIDO QUE O COMPROU, do fundador em 07/10/2026: *"O visualizador do pdf deve ser bem otimizado
 * para todos os dispositivos, utilize uma forma ja consolidada pra isso (no manual ou qualquer lugar
 * que tenha que ver um pdf no sistema)"*.
 *
 * O DEFEITO QUE ELE VIU, E A CAUSA E DE ARQUITETURA: o PDF era desenhado num `<iframe>` com
 * `#toolbar=0&navpanes=0&zoom=90`, copiado de `portal/index.html:3242`. Quem desenhava o PDF ali era
 * o visualizador DO PROPRIO NAVEGADOR, e nao codigo nosso. Em navegador de celular esse visualizador
 * desenha a primeira pagina e nao rola para as seguintes, e o `#toolbar=0` tirava a barra, que era a
 * unica navegacao que restava. As duas coisas somadas davam o que ele leu no celular: uma pagina so,
 * sem como passar.
 *
 * POR QUE ISSO NAO SE CONSERTA NO `<iframe>`: o que desenha e o navegador, e a pagina nao tem como
 * mandar nele. O conserto e PARAR DE DELEGAR o desenho. Aqui o PDF e lido pelo pdf.js e cada pagina
 * e desenhada num `<canvas>` nosso, entao a navegacao passa a ser codigo desta casa e vale igual em
 * todo aparelho e em todo navegador, que e a diretriz primaria de paridade de funcao.
 *
 * A BIBLIOTECA E O pdf.js DA MOZILLA (`pdfjs-dist`), o visualizador que vai dentro do Firefox:
 * 37.544.336 downloads por semana e licenca Apache-2.0, medidos em 07/10/2026. ELE ENTRA DIRETO, e
 * nao pelo embrulho `react-pdf`, porque o `react-pdf@11` exige `react ^19.0.0` e este monorepo fixa
 * 18.3.1 no catalogo: entrar por ele seria adotar a linha 10.x ja superada, que por sua vez prende o
 * pdf.js em 5.4.296. A comparacao inteira, com os quatro candidatos e os numeros de cada um, esta em
 * `docs/progresso/PROGRESSO-VISUALIZADOR-PDF.md`.
 *
 * POR QUE ELA MORA EM `packages/ui` E NAO DENTRO DE UM APP: sao DOIS lugares que mostram PDF, a aba
 * "Manual Foto Modelo" da cliente e a sub-aba "Manual Foto" do painel. Escrita num dos dois, o outro
 * copiaria, e copia envelhece de um lado so.
 */

// Estado, memoria entre renders, referencia ao elemento e efeito.
import { useCallback, useEffect, useRef, useState } from 'react';
// O tipo do evento de teclado vem IMPORTADO, e nao pelo `React.` global: com `isolatedModules`, o
// namespace global existe por acidente da configuracao do JSX, e depender dele quebra calado no dia
// em que a configuracao mudar.
import type { KeyboardEvent as EventoDeTeclado } from 'react';
/*
 * OS TIPOS VEM DO PACOTE, E SO OS TIPOS.
 *
 * `import type` e apagado na compilacao, entao estas duas linhas custam ZERO no pacote publicado. O
 * codigo do pdf.js entra por import DINAMICO, mais abaixo, e o porque esta la.
 */
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
// O botao da plataforma. O lint recusa `<button>` solto, e e por ele que o alvo de toque de 44px, a
// forma de capsula e o anel de foco nao dependem de quem escreve a tela.
import { Botao } from './Button.js';
// A seta do conjunto de icones. Emoji nao entra na tela, por regra dura: icone e SVG do Chassi.
import { IconeChevron } from './Icones.js';

/**
 * O WORKER VAI NO PACOTE PUBLICADO, E NAO POR CDN.
 *
 * O pdf.js interpreta o arquivo dentro de um Web Worker, para a pagina nao congelar enquanto ele le
 * um PDF grande. O sufixo `?url` manda o Vite EMITIR este arquivo como ativo do build e devolver o
 * endereco dele com hash, servido pelo nosso proprio pacote.
 *
 * CDN FICA DE FORA DE PROPOSITO, e sao tres motivos: quebra sem rede, cria dependencia externa que
 * ninguem declarou, e faria o endereco assinado do manual da cliente passar por um terceiro.
 *
 * `.min.mjs` E NAO `.mjs`: 1.235 KB contra 2.177 KB, medidos no pacote 6.4.299. E o mesmo codigo.
 */
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * O CODIGO DO pdf.js ENTRA POR IMPORT DINAMICO, E O NUMERO DECIDIU ISSO.
 *
 * MEDIDO EM 07/10/2026: o pacote publicado do app da cliente tem 575,63 kB (174,12 kB comprimido).
 * O pdf.js soma perto de 480 kB a isso. Esta peca e exportada pelo barril de `@complexity/ui`, e com
 * import ESTATICO esse peso entraria em TODO app que importa qualquer peca do Chassi, inclusive na
 * tela de login do admin, que nunca mostra PDF. Seria quase dobrar o pacote de toda tela da
 * plataforma por causa de duas, e o oposto do que o pedido do fundador diz: *"bem otimizado para
 * todos os dispositivos"*.
 *
 * COM O IMPORT DINAMICO o empacotador poe o pdf.js num pedaco SEPARADO, buscado so quando esta peca
 * e desenhada de verdade. Quem abre o painel nao paga por ele; quem abre o manual paga uma vez.
 *
 * A PROMESSA E GUARDADA, e nao refeita: duas telas com visualizador na mesma sessao, ou o mesmo
 * manual reaberto, reaproveitam o modulo ja carregado em vez de pedir de novo.
 */
let moduloDoPdfJs: Promise<typeof import('pdfjs-dist')> | null = null;

function carregarPdfJs() {
  if (!moduloDoPdfJs) {
    moduloDoPdfJs = import('pdfjs-dist').then((modulo) => {
      /*
       * O WORKER E APONTADO UMA VEZ, aqui dentro: `GlobalWorkerOptions` e global do pdf.js, e
       * atribuir a ele a cada abertura repetiria a mesma escrita sem efeito nenhum. Como isto mora
       * dentro da promessa guardada, roda exatamente uma vez por carga da pagina.
       */
      modulo.GlobalWorkerOptions.workerSrc = workerUrl;
      return modulo;
    });
  }
  return moduloDoPdfJs;
}

/**
 * O TETO DA DENSIDADE DE TELA, e o numero tem motivo.
 *
 * Tela de celular costuma reportar `devicePixelRatio` 3, e desenhar o canvas com 3 vezes a largura em
 * cada eixo custa NOVE vezes a memoria. Numa pagina A4 a 390px isso passa de 1,2 milhao de pixels
 * para 11 milhoes, e e onde o navegador de celular desiste do canvas e entrega um quadro branco.
 * Em 2 o texto ja fica nitido, e e o valor que o proprio pdf.js usa como teto no visualizador dele.
 */
const TETO_DE_DENSIDADE = 2;

export interface VisualizadorPdfProps {
  /**
   * O endereco do PDF. Nos dois usos de hoje ele e um link ASSINADO do Storage, com validade de uma
   * hora, e por isso a peca nao o guarda nem o monta: ela recebe pronto e so le.
   */
  readonly endereco: string;
  /**
   * O nome do documento, para quem usa leitor de tela. Sem ele a pessoa ouviria apenas "imagem", que
   * nao diz o que esta na tela.
   */
  readonly titulo: string;
  /** O `data-testid` do recipiente, para a suite achar esta peca dentro da tela que a usa. */
  readonly testid?: string;
}

/** O que a peca esta fazendo agora. Os tres estados sao exclusivos, e nunca dois ao mesmo tempo. */
type Situacao =
  // Abrindo o documento. E so o primeiro carregamento: trocar de pagina nao volta para ca.
  | { readonly tipo: 'abrindo' }
  // Aberto, com o total de paginas que o arquivo declara.
  | { readonly tipo: 'aberto'; readonly totalDePaginas: number }
  /*
   * NAO CONSEGUIMOS ABRIR, e este estado existe por regra dura: "estado vazio nunca pode parecer
   * defeito do sistema". Aqui a situacao e o OPOSTO do estado vazio: se esta peca recebeu um
   * endereco e nao conseguiu ler, houve falha de verdade, e dizer isso e o certo.
   *
   * O QUE NAO ENTRA NELE E O CODIGO DE PROTOCOLO: a mensagem do pdf.js costuma carregar o status
   * HTTP, e numero de protocolo na tela nao ajuda ninguem a decidir o que fazer e ainda assusta. O
   * tecnico vai para o console, e a tela recebe a frase que diz o que houve e o que fazer agora.
   */
  | { readonly tipo: 'falhou' };

export function VisualizadorPdf({ endereco, titulo, testid }: VisualizadorPdfProps) {
  // O recipiente, de quem sai a largura que a pagina acompanha.
  const recipienteRef = useRef<HTMLDivElement | null>(null);
  // O canvas onde a pagina e desenhada.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /*
   * O DOCUMENTO ABERTO MORA NUMA REFERENCIA, e nao em estado.
   *
   * Ele nao e desenhado: e a fonte de onde as paginas saem. Guardado em estado, cada troca de pagina
   * passaria por uma comparacao de objeto que nunca muda, e o `destroy()` da limpeza perderia a
   * ultima versao dele.
   */
  const documentoRef = useRef<PDFDocumentProxy | null>(null);
  /*
   * A TAREFA DE DESENHO EM CURSO, guardada para ser CANCELADA.
   *
   * O pdf.js recusa dois desenhos no mesmo canvas ao mesmo tempo, e sem este cancelamento trocar de
   * pagina depressa, ou girar o aparelho durante um desenho, produz um erro que a tela leria como
   * falha de leitura do arquivo. Quem cancela e quem troca, e nao o navegador.
   */
  const tarefaRef = useRef<RenderTask | null>(null);

  const [situacao, setSituacao] = useState<Situacao>({ tipo: 'abrindo' });
  // A pagina na tela, contada de 1 como a pessoa conta, e nao de 0 como o indice.
  const [pagina, setPagina] = useState(1);
  /*
   * A LARGURA UTIL DO RECIPIENTE, em pixels de CSS.
   *
   * `0` enquanto nao foi medida: desenhar antes da medicao produziria uma pagina do tamanho errado,
   * que depois salta ao ser corrigida.
   */
  const [largura, setLargura] = useState(0);

  /*
   * A LARGURA VEM DO `ResizeObserver`, e nao do tamanho da janela.
   *
   * O que decide o tamanho da pagina e o espaco que ESTA PECA recebeu, e nao o da tela: a mesma peca
   * vive dentro do conteudo da cliente e dentro de uma sub-aba do painel, que tem menus ao lado. Ler
   * a janela acertaria num caso e erraria no outro, e erraria calado.
   *
   * Ele tambem cobre o giro do aparelho e a gaveta do menu abrindo, que mudam a largura sem a janela
   * mudar de tamanho.
   */
  useEffect(() => {
    const alvo = recipienteRef.current;
    if (!alvo) return;
    const observador = new ResizeObserver((entradas) => {
      const medida = entradas[0]?.contentRect.width ?? 0;
      // Arredonda para baixo: largura fracionada produz canvas de tamanho fracionado, e o navegador
      // reamostra a imagem, o que aparece como texto levemente borrado.
      setLargura(Math.floor(medida));
    });
    observador.observe(alvo);
    return () => observador.disconnect();
  }, []);

  /*
   * ABRIR O DOCUMENTO, uma vez por endereco.
   *
   * O `endereco` e um link assinado, entao trocar de arquivo troca o endereco, e e por isso que ele
   * e a unica dependencia: o efeito nao roda de novo ao trocar de pagina.
   */
  useEffect(() => {
    // `valida` desarma o que chegar depois de o efeito ter sido desfeito. Sem isto, um endereco que
    // demora e e substituido grava o estado do arquivo ANTIGO sobre o do novo.
    let valida = true;
    /*
     * A TAREFA DE ABERTURA MORA NUMA VARIAVEL QUE A LIMPEZA ALCANCA, e nao dentro do bloco assincrono.
     *
     * Ela so passa a existir depois de o pdf.js chegar pelo import dinamico, e a limpeza pode rodar
     * ANTES disso, se a pessoa sair da tela enquanto o modulo carrega. Declarada aqui, a limpeza a
     * encontra quando ela existe e nao faz nada quando nao existe.
     */
    let abertura: PDFDocumentLoadingTask | null = null;
    // Toda troca de arquivo volta para a primeira pagina: continuar na pagina 7 de um documento que
    // tem 3 mostraria um erro em vez de um documento.
    setPagina(1);
    setSituacao({ tipo: 'abrindo' });
    void (async () => {
      // O pdf.js por import dinamico. Na segunda vez isto resolve na hora, do modulo ja guardado.
      const pdf = await carregarPdfJs();
      // Saiu da tela enquanto o modulo vinha: nao abre arquivo nenhum.
      if (!valida) return;
      /*
       * `{ url }` E NAO A STRING SOLTA: na versao 6 do pdf.js o `getDocument` recebe
       * `DocumentInitParameters`, e passar o endereco cru nao compila mais (medido no
       * `types/src/display/api.d.ts:750` do 6.4.299).
       */
      abertura = pdf.getDocument({ url: endereco });
      const documento = await abertura.promise;
      /*
       * CHEGOU DEPOIS DA LIMPEZA: nao ha nada a fechar aqui, e isto foi medido.
       *
       * Quem encerra e o `abertura.destroy()` da limpeza, que derruba o transporte e o documento
       * junto. O `PDFDocumentProxy` da versao 6 NAO tem `destroy()`, so `cleanup()`, e chamar o que
       * nao existe foi um dos dois erros que o typecheck pegou aqui.
       */
      if (!valida) return;
      documentoRef.current = documento;
      setSituacao({ tipo: 'aberto', totalDePaginas: documento.numPages });
    })().catch((erro: unknown) => {
      if (!valida) return;
      // O TECNICO VAI PARA O CONSOLE, e nao para a tela: e aqui que o status HTTP e o motivo real
      // ficam alcancaveis para quem mantem, sem assustar quem usa.
      console.error('Nao foi possivel abrir o PDF:', erro);
      setSituacao({ tipo: 'falhou' });
    });
    return () => {
      valida = false;
      // O desenho em curso para antes do documento: cancelar depois de fechar levanta erro.
      tarefaRef.current?.cancel();
      tarefaRef.current = null;
      // `destroy` do loading task tambem encerra o documento que ele produziu. `?.` porque a
      // limpeza pode chegar antes de o import dinamico terminar.
      void abertura?.destroy();
      documentoRef.current = null;
    };
  }, [endereco]);

  /*
   * DESENHAR A PAGINA ATUAL, e so ela.
   *
   * E AQUI QUE MORA O CARREGAMENTO PREGUICOSO: o pdf.js le o arquivo por faixas, e `getPage` busca
   * do servidor apenas o que aquela pagina precisa. Um manual de 20 paginas aberto na primeira nao
   * baixa as outras 19, e e por isso que esta peca abre rapido em rede de celular.
   */
  useEffect(() => {
    const documento = documentoRef.current;
    // Sem documento aberto, ou antes da primeira medicao de largura, nao ha o que desenhar.
    if (situacao.tipo !== 'aberto' || !documento || largura <= 0) return;
    let valida = true;
    /*
     * O MODULO E PEDIDO DE NOVO AQUI, e nao guardado em estado: `carregarPdfJs` devolve a MESMA
     * promessa ja resolvida, entao o custo e zero, e a alternativa seria guardar o modulo num
     * estado que redesenharia a tela sem nada ter mudado na tela.
     *
     * Ele e necessario por causa do `RenderingCancelledException` do `catch`: distinguir
     * cancelamento de falha exige a CLASSE, e ela vive dentro do modulo carregado.
     */
    void (async () => {
      const pdf = await carregarPdfJs();
      if (!valida) return;
      /*
       * O DESENHO ANTERIOR PARA ANTES DE O NOVO COMECAR.
       *
       * Dois `render` no mesmo canvas ao mesmo tempo e recusado pelo pdf.js. Quem troca de pagina
       * depressa, ou gira o aparelho no meio de um desenho, cai exatamente nisso.
       */
      tarefaRef.current?.cancel();
      tarefaRef.current = null;
      const folha = await documento.getPage(pagina);
      if (!valida) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      /*
       * A ESCALA SAI DA LARGURA DO RECIPIENTE, e e isto que faz a pagina caber em qualquer aparelho
       * sem rolagem horizontal. `scale: 1` da o tamanho natural do PDF em pontos; a razao entre a
       * largura que temos e essa e o quanto a pagina precisa crescer ou encolher.
       */
      const natural = folha.getViewport({ scale: 1 });
      // Densidade limitada: o porque do teto esta em `TETO_DE_DENSIDADE`.
      const densidade = Math.min(window.devicePixelRatio || 1, TETO_DE_DENSIDADE);
      const viewport = folha.getViewport({ scale: (largura / natural.width) * densidade });
      /*
       * O CANVAS TEM DOIS TAMANHOS, e confundi-los e o defeito classico de PDF borrado.
       *
       * `width` e `height` sao os PIXELS DE VERDADE que o pdf.js pinta, e por isso levam a densidade.
       * O tamanho em CSS vem do `className` abaixo (`w-full h-auto`), que encolhe a imagem de volta
       * para a largura do recipiente. Sem os dois, ou o texto sai borrado, ou a pagina estoura a
       * largura da tela.
       */
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      /*
       * `canvas` E NAO `canvasContext`, e isto mudou na versao 6 do pdf.js (medido no
       * `types/src/display/api.d.ts` do 6.4.299): `canvas` e o parametro recomendado, e o
       * `canvasContext` ficou como compatibilidade para tras, exigindo `canvas: null` junto.
       */
      const tarefa = folha.render({ canvas, viewport });
      tarefaRef.current = tarefa;
      try {
        await tarefa.promise;
      } catch (erro) {
        /*
         * CANCELAMENTO NAO E FALHA, e tratar os dois igual e o que faria a tela acusar defeito
         * quando a pessoa apenas trocou de pagina depressa. E a mesma familia do 404 de recurso
         * unico que a regra dura separa de falha de verdade: o sistema respondeu, e respondeu certo.
         *
         * A COMPARACAO E AQUI DENTRO, e nao no `catch` de fora, porque a classe vive no modulo que
         * o import dinamico trouxe, e `pdf` so existe neste escopo.
         */
        if (erro instanceof pdf.RenderingCancelledException) return;
        // Falha de verdade: sobe para o tratamento de baixo, que e o unico que acende a tela de erro.
        throw erro;
      }
    })().catch((erro: unknown) => {
      if (!valida) return;
      console.error('Nao foi possivel desenhar a pagina do PDF:', erro);
      setSituacao({ tipo: 'falhou' });
    });
    return () => {
      valida = false;
    };
  }, [situacao, pagina, largura]);

  // O total, lido do arquivo. Fora do estado "aberto" ele e 0, e os controles nao aparecem.
  const total = situacao.tipo === 'aberto' ? situacao.totalDePaginas : 0;

  // Anda uma pagina, sem passar dos limites: `Math.min` e `Math.max` fazem o botao da ponta nao
  // precisar de condicao propria para nao estourar.
  const irPara = useCallback(
    (destino: number) => setPagina(() => Math.min(Math.max(destino, 1), Math.max(total, 1))),
    [total],
  );

  /*
   * AS SETAS DO TECLADO, para quem usa computador e nao quer ir ao mouse.
   *
   * No recipiente, e nao na janela: capturada na janela, esta peca roubaria a seta de qualquer outra
   * coisa da tela que a usasse. O `tabIndex` abaixo e o que permite o recipiente receber o foco.
   */
  const aoTeclar = useCallback(
    (evento: EventoDeTeclado<HTMLDivElement>) => {
      if (evento.key === 'ArrowRight' || evento.key === 'PageDown') {
        evento.preventDefault();
        irPara(pagina + 1);
        return;
      }
      if (evento.key === 'ArrowLeft' || evento.key === 'PageUp') {
        evento.preventDefault();
        irPara(pagina - 1);
      }
    },
    [irPara, pagina],
  );

  return (
    <div
      ref={recipienteRef}
      data-testid={testid}
      // `tabIndex={0}` para as setas do teclado chegarem aqui. `role="group"` com o nome do
      // documento faz o leitor de tela anunciar o que e este bloco antes de ler o conteudo.
      tabIndex={0}
      role="group"
      aria-label={titulo}
      onKeyDown={aoTeclar}
      className="flex w-full flex-col gap-cx-3 rounded-cx-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
    >
      {/*
        A AREA DO DESENHO.

        `overflow-hidden` com `w-full` e o par que garante o que a regra cobra: `scrollWidth` igual a
        `clientWidth`, ou seja, sem rolagem horizontal. A escala calculada acima ja faz a pagina
        caber; isto impede que um arredondamento de meio pixel crie uma barra de rolagem.
      */}
      <div className="w-full overflow-hidden rounded-cx-md border border-borda bg-superficie-afundada">
        {situacao.tipo === 'abrindo' && (
          /*
            CARREGANDO, e ele ocupa ALTURA: um bloco de altura zero faria a tela saltar quando a
            pagina chegasse, e salto de layout e o que faz a pessoa perder o lugar onde estava lendo.
            `aria-busy` diz ao leitor de tela que ali vem conteudo, em vez de deixar o silencio.
          */
          <div
            aria-busy="true"
            data-testid="pdf-carregando"
            className="flex min-h-[320px] items-center justify-center p-cx-6 text-cx-sm text-texto-suave"
          >
            Carregando o documento...
          </div>
        )}
        {situacao.tipo === 'falhou' && (
          /*
            NAO CONSEGUIMOS ABRIR.

            `role="status"` e nao `alert`: a pessoa ja esta olhando para este bloco, e `alert`
            interromperia a leitura dela para dizer o que ela acabou de ver.

            O TEXTO DIZ O QUE FAZER, e nao o que o protocolo devolveu. Sem codigo HTTP, sem a
            mensagem crua da biblioteca, e sem a palavra "erro" sozinha, que e a que faz quem opera
            concluir que o sistema caiu.
          */
          <div
            role="status"
            data-testid="pdf-falhou"
            className="flex min-h-[320px] flex-col items-center justify-center gap-cx-2 p-cx-6 text-center"
          >
            <p className="text-cx-base font-medium text-texto">Não foi possível exibir o documento</p>
            <p className="text-cx-sm text-texto-suave">
              O arquivo pode ter expirado nesta pagina. Atualize a pagina para tentar de novo.
            </p>
          </div>
        )}
        {/*
          O CANVAS FICA NO DOM EM TODOS OS ESTADOS, e `hidden` apenas o esconde.

          Desmontado enquanto carrega, `canvasRef.current` seria `null` no instante em que o desenho
          termina, e a primeira pagina nunca apareceria. E o defeito que a condicao obvia produz.

          `block` tira o espaco fantasma que o navegador reserva embaixo de elemento em linha, e
          `w-full h-auto` e a metade em CSS do tamanho do canvas explicada no desenho acima.
        */}
        <canvas
          ref={canvasRef}
          data-testid="pdf-pagina"
          aria-label={titulo}
          role="img"
          className={situacao.tipo === 'aberto' ? 'block h-auto w-full' : 'hidden'}
        />
      </div>

      {/*
        OS CONTROLES SO APARECEM COM O DOCUMENTO ABERTO: botao de navegacao em cima de um documento
        que nao abriu promete o que nao cumpre.
      */}
      {situacao.tipo === 'aberto' && (
        <div className="flex flex-wrap items-center justify-center gap-cx-3">
          {/*
            `tamanho="icone"` e o que entrega os 44px nos DOIS eixos, pelo `min-h-toque` e
            `min-w-toque` do preset. Alvo de toque e requisito de celular, e nao enfeite.

            `disabled` na ponta, e nao botao escondido: botao que desaparece move os vizinhos de
            lugar, e a pessoa erra o alvo que estava mirando.
          */}
          <Botao
            variante="contorno"
            tamanho="icone"
            onClick={() => irPara(pagina - 1)}
            disabled={pagina <= 1}
            data-testid="pdf-anterior"
            // O rotulo escrito vive no `aria-label`, porque o conteudo e um desenho. Sem ele, o
            // leitor de tela anuncia "botao" e a pessoa nao sabe para onde ele vai.
            aria-label="Página anterior"
          >
            {/* A seta do conjunto aponta para BAIXO: girada em 90 graus ela aponta para a esquerda. */}
            <IconeChevron className="h-5 w-5 rotate-90" aria-hidden="true" />
          </Botao>
          {/*
            O CONTADOR, e ele e `aria-live="polite"`: quem usa leitor de tela precisa ouvir que a
            pagina mudou, porque a mudanca acontece numa imagem, que nao se anuncia sozinha.
            "polite" e nao "assertive" para a troca nao cortar o que estiver sendo lido.
          */}
          <p
            aria-live="polite"
            data-testid="pdf-contador"
            className="min-w-[7rem] text-center text-cx-sm tabular-nums text-texto-suave"
          >
            {`Página ${pagina} de ${total}`}
          </p>
          <Botao
            variante="contorno"
            tamanho="icone"
            onClick={() => irPara(pagina + 1)}
            disabled={pagina >= total}
            data-testid="pdf-proxima"
            aria-label="Página seguinte"
          >
            {/* Girada para o outro lado: a mesma seta serve aos dois sentidos. */}
            <IconeChevron className="h-5 w-5 -rotate-90" aria-hidden="true" />
          </Botao>
        </div>
      )}
    </div>
  );
}
